import { canAnywhere } from '@hrms/shared';
import type { EmployeeLimit } from '../../billing/planLimit.js';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';
import { useSetupStatus } from '../panel/useSetupStatus.js';

/** How many people besides the owner a new company adds before attendance is "on". */
export const FIRST_STAFF_TARGET = 3;

export type ActivationStepKey = 'workspace' | 'team' | 'punch';

export interface ActivationStep {
  key: ActivationStepKey;
  done: boolean;
}

export interface ActivationProgress {
  steps: ActivationStep[];
  done: number;
  total: number;
  /** Whole percent, for the label beside the bar. */
  percent: number;
  /** The first step not done ("You are here"); null once every step is done. */
  current: ActivationStepKey | null;
}

/** Whether a team member has clocked in yet; `n/a` when attendance is not on the plan. */
export type PunchState = 'done' | 'pending' | 'n/a';

/**
 * The "Get your HRMS active" steps: the workspace (always done), the first three employees, and
 * the first mobile punch-in. The punch step drops out (2 steps) when attendance is not on the plan.
 */
export function activationProgress(teamSize: number, punch: PunchState): ActivationProgress {
  const steps: ActivationStep[] = [
    { key: 'workspace', done: true },
    { key: 'team', done: teamSize >= FIRST_STAFF_TARGET },
  ];
  if (punch !== 'n/a') steps.push({ key: 'punch', done: punch === 'done' });
  const done = steps.filter((step) => step.done).length;
  return {
    steps,
    done,
    total: steps.length,
    percent: Math.round((done / steps.length) * 100),
    current: steps.find((step) => !step.done)?.key ?? null,
  };
}

export interface ActivationStatus {
  /** People with a current employment, the owner not counted (same count as the trial cap). */
  teamSize: number;
  punch: PunchState;
  employeeLimit: EmployeeLimit | null;
}

/**
 * The activation numbers from setup-status, for someone who can add employees (and read the
 * setup status they come from). Null while loading, on an error, for anyone else, and against an
 * API that does not send `teamSize` yet, so nothing shows a wrong count.
 */
export function useActivationStatus(): ActivationStatus | null {
  const { actor } = useAuthenticatedSession();
  const allowed = canAnywhere(actor, 'employee:create') && canAnywhere(actor, 'org.settings:read');
  const status = useSetupStatus(allowed);
  if (!allowed || status.data?.status !== 200) return null;
  const body = status.data.body;
  if (typeof body.teamSize !== 'number') return null;
  const punchStep = body.steps.find((step) => step.key === 'first_punch');
  const punch: PunchState =
    !punchStep || !punchStep.applicable ? 'n/a' : punchStep.done ? 'done' : 'pending';
  return { teamSize: body.teamSize, punch, employeeLimit: body.employeeLimit ?? null };
}
